import axios from 'axios';
import {
  ACTIVATE_USER,
  ASSIGN_USER_TO_PROJECT,
  DEACTIVATE_USER,
  get_request_config,
} from '@/utils/system_constants/constant.js';
import {
  assign_form_defaults,
  parse_api_data,
} from './user_management_helpers';

export default {
  async open_assign_user_modal(user) {
    if (user.is_active === false) {
      this.show_warning(
        'User Deactivated',
        `You cannot assign ${user.name} because this user is deactivated. Please activate the user first.`
      );
      return;
    }

    this.assign_form = assign_form_defaults();
    this.assign_form.user = user;
    this.show_project_dropdown = false;

    if (!this.projects.length) {
      await this.fetch_projects();
    }

    const assigned_project_ids = await this.fetch_assigned_project_ids(user.id);
    this.assign_form.project_ids = assigned_project_ids;
    this.assign_form.original_project_ids = assigned_project_ids;
  },

  toggle_project_dropdown() {
    if (this.projects_loading || this.assigned_projects_loading) return;
    this.show_project_dropdown = !this.show_project_dropdown;
  },

  select_project(project) {
    const project_id = String(project?.id || '');
    if (!project_id) return;

    const selected_ids = new Set(
      Array.isArray(this.assign_form.project_ids)
        ? this.assign_form.project_ids.map(String)
        : []
    );

    if (selected_ids.has(project_id)) {
      selected_ids.delete(project_id);
    } else {
      selected_ids.add(project_id);
    }

    this.assign_form.project_ids = Array.from(selected_ids);
  },

  is_project_selected(project_id) {
    return (Array.isArray(this.assign_form.project_ids) ? this.assign_form.project_ids : [])
      .map(String)
      .includes(String(project_id));
  },

  toggle_user_status(user) {
    const should_activate = user.is_active === false;
    const action_label = should_activate ? 'activate' : 'deactivate';

    this.show_confirm(
      `${should_activate ? 'Activate' : 'Deactivate'} User`,
      `Are you sure you want to ${action_label} ${user.name}?`,
      async () => {
        const loader = await this.show_loader(
          `${should_activate ? 'Activating' : 'Deactivating'} user...`
        );

        try {
          const response = await axios.post(
            should_activate ? ACTIVATE_USER : DEACTIVATE_USER,
            { user_id: user.id },
            get_request_config()
          );
          const response_data = parse_api_data(response.data);

          if (response_data?.status && response_data.status !== 'success') {
            throw new Error(response_data.message || 'User status update failed.');
          }

          await this.fetch_users();
          this.show_success(response_data.message || 'User status updated.');
        } catch (error) {
          this.show_error(this.parse_error_message(error, 'Failed to update user status.'));
        } finally {
          this.close_loader(loader);
        }
      }
    );
  },

  async assign_user_to_project() {
    if (!this.assign_form.user?.id) {
      this.show_error('Please select a user.');
      return;
    }

    const selected_project_ids = Array.isArray(this.assign_form.project_ids)
      ? this.assign_form.project_ids
      : [];
    const original_project_ids = Array.isArray(this.assign_form.original_project_ids)
      ? this.assign_form.original_project_ids
      : [];

    if (!selected_project_ids.length && !original_project_ids.length) {
      this.show_error('Please select at least one project.');
      return;
    }

    const original_ids = new Set(original_project_ids.map(String));
    const selected_ids = new Set(selected_project_ids.map(String));
    const changed_project_ids = [
      ...selected_project_ids.filter((project_id) => !original_ids.has(String(project_id))),
      ...original_project_ids.filter((project_id) => !selected_ids.has(String(project_id))),
    ];

    if (!changed_project_ids.length) {
      this.close_modal('assign_user_modal');
      this.assign_form = assign_form_defaults();
      this.show_project_dropdown = false;
      this.show_success('User project assignments are already up to date.');
      return;
    }

    const assigner_id = this.$store.getters.currentUserId;
    if (!assigner_id) {
      this.show_error('Current user could not be resolved.');
      return;
    }

    this.assigning_user = true;
    const loader = await this.show_loader('Assigning user...');

    try {
      const responses = await Promise.all(
        changed_project_ids.map((project_id) =>
          axios.post(
            ASSIGN_USER_TO_PROJECT,
            {
              project: Number(project_id),
              assigner: Number(assigner_id),
              assignee: Number(this.assign_form.user.id),
            },
            get_request_config()
          )
        )
      );

      responses.forEach((response) => {
        const response_data = parse_api_data(response.data);
        if (response_data?.status && response_data.status !== 'success') {
          throw new Error(response_data.message || 'Project assignment failed.');
        }
      });

      this.close_modal('assign_user_modal');
      this.assign_form = assign_form_defaults();
      this.show_project_dropdown = false;
      this.show_success(
        `User assignment updated for ${changed_project_ids.length} project${changed_project_ids.length === 1 ? '' : 's'}.`
      );
    } catch (error) {
      this.show_error(this.parse_error_message(error, 'Failed to assign user.'));
    } finally {
      this.assigning_user = false;
      this.close_loader(loader);
    }
  },
};
